import { useEffect, useState } from "react";
import {
  View,
  Text,
  Flex,
  ActionButton,
  TooltipTrigger,
  Tooltip,
} from "@adobe/react-spectrum";
import { useSelector } from "react-redux";
import Emitter from "../../../emitter";
import {
  API_CALL_HOLD,
  API_CALL_PLAY,
  CALLBACK,
  CHANNEL_TYPES,
  API_CALL_HANGUP,
  API_CALL_REMOVE_CONNECTION,
  API_CALL_CONFERENCE_PARTICIPANT_MUTE,
} from "../../../constants/constants";
import { getConnectionIdByPhoneNumber } from "../../../amazon-connect/api/call";
import { RootState } from "../../../store/storeConfig";
import { IntervalType, SpeedDialData } from "../../../types/generic.types";
import IncomingCallIcon from "../../../assets/callIncomingIcon.svg";
import OutgoingCallIcon from "../../../assets/CallOutgoingIcon.svg";
import EyeIcon from "../../../assets/eye-icon.svg";
import { setCurrentSessionTab } from "../../../actions/session";
import storeRTK from "../../../store/storeRTK";
import { formattedTime } from "../../../utils";
import { tabAccessControl } from "../../../utils/utils";
import {
  CallHangupIcon,
  MuteIcon,
  PauseIcon,
  PlayIcon,
  UnMmuteIcon,
} from "../../common/Icons";
import { setCallState } from "../../../actions/call";

export interface LiveCallProps {
  callData?: any;
  callType?: "primary" | "conference" | "consult";
  showMerge?: boolean;
  isConferenceCall?: boolean;
}

const LiveCall = ({
  callData,
  callType: _callType = "primary",
}: LiveCallProps = {}) => {
  const [callTime, setCallTime] = useState(0);
  const globalCall = useSelector((state: RootState) => state.call);
  const callback = useSelector((state: RootState) => state.callback);
  const voicemail = useSelector((state: RootState) => state.voicemail);
  const conference = useSelector((state: RootState) => state.conference);
  const speedDials = useSelector(
    (state: RootState) => state.history.speedDials
  );
  const isAgentSelfCallHeld = useSelector(
    (state: RootState) => state.session.isAgentSelfCallHeld
  );

  const call = callData || globalCall;

  useEffect(() => {
    let timerId: IntervalType = null;
    if (call?.self?.state === "ESTABLISHED") {
      const startTime = call?.self.connectedTime;
      timerId = setInterval(() => {
        setCallTime(Date.now() - startTime);
      }, 1000);
    } else {
      if (timerId !== null) {
        clearInterval(timerId);
      }
    }
    return () => {
      if (timerId !== null) {
        clearInterval(timerId);
      }
    };
  }, [call?.self?.state]);

  const hangupCallHandler = () => {
    if (conference && conference.id && call?.self?.state === "ESTABLISHED") {
      const primaryPhoneNumber =
        call?.customer?.phoneNumber || call?.customer?.displayName;
      const connectionId = getConnectionIdByPhoneNumber(primaryPhoneNumber);
      if (connectionId) {
        console.log(
          "LiveCall: Removing primary connection with ID:",
          connectionId,
          "for phone:",
          primaryPhoneNumber
        );
        Emitter.emit(API_CALL_REMOVE_CONNECTION, {
          body: {
            connectionId,
            conversationId: call.id,
          },
        });
      } else {
        Emitter.emit(API_CALL_HANGUP, { body: { conversationId: call.id } });
      }
    } else {
      console.log(
        "LiveCall: No conference or not established, using default hangup"
      );
      Emitter.emit(API_CALL_HANGUP, { body: { conversationId: call.id } });
      if (callback?.id)
        storeRTK.dispatch(setCurrentSessionTab({ id: CALLBACK }));
      if (voicemail?.id)
        storeRTK.dispatch(
          setCurrentSessionTab({ id: CHANNEL_TYPES.VOICEMAIL })
        );
    }
  };

  const muteCallHandler = () => {
    if (call.isSilentMonitor && !call.isBarge) return;

    // commented out for conference participant mute
    // Emitter.emit(API_CALL_MUTE, {
    //   body: { mute: !call?.self?.muted, conversationId: call.id },
    // });
    //Below will mute specific participant in a conference
    Emitter.emit(API_CALL_CONFERENCE_PARTICIPANT_MUTE, {
      body: { conversationId: call.id, connectionId: call.self.id },
    });
  };

  const toggleHold = () => {
    if (!call?.id) return;
    const isCurrentlyHeld = Boolean(call.self.held);
    const nextHeld = !isCurrentlyHeld;
    const event = nextHeld ? API_CALL_PLAY : API_CALL_HOLD;
    Emitter.emit(event, { body: { held: nextHeld, conversationId: call.id } });
    storeRTK.dispatch(
      setCallState({
        ...call,
        self: {
          ...call.self,
          held: nextHeld,
        },
        customer: {
          ...call.customer,
          held: nextHeld,
        },
      })
    );
  };

  const disableTransfer = (type?: string) => {
    const isSilentMonitor = call?.isSilentMonitor;
    const noTransferEndpoints = call?.allowedTransferEndpoints?.length === 0;
    const callConnected = call?.self?.state === "ESTABLISHED";

    return type === "transfer"
      ? isSilentMonitor || noTransferEndpoints
      : !callConnected;
  };

  const getCustomerName = () => {
    const phoneNumber =
      call?.customer?.attributes?.Customer_ANI ||
      call?.customer?.displayName ||
      call?.customer?.phoneNumber ||
      call?.customer?.address;

    const speedDialEntry = speedDials?.find(
      (entry: SpeedDialData) => entry?.phoneNumber === phoneNumber
    );

    if (speedDialEntry && speedDialEntry.firstName && speedDialEntry.lastName) {
      return `${speedDialEntry.firstName} ${speedDialEntry.lastName}`;
    }

    if (
      call?.customer?.attributes?.FIRSTNAME &&
      call?.customer?.attributes?.LASTNAME
    ) {
      return `${call?.customer?.attributes?.FIRSTNAME} ${call?.customer?.attributes?.LASTNAME}`;
    }

    return "";
  };

  const getCustomerPhone = () => {
    return (
      call?.customer?.attributes?.Customer_ANI ||
      call?.customer?.displayName ||
      call?.customer?.phoneNumber ||
      call?.customer?.address
    );
  };

  const hasCustomerName = () => {
    const phoneNumber =
      call?.customer?.attributes?.Customer_ANI ||
      call?.customer?.displayName ||
      call?.customer?.phoneNumber ||
      call?.customer?.address;

    const speedDialEntry = speedDials?.find(
      (entry: SpeedDialData) => entry?.phoneNumber === phoneNumber
    );
    if (speedDialEntry && speedDialEntry.firstName && speedDialEntry.lastName) {
      return true;
    }
    return !!(
      call?.customer?.attributes?.FIRSTNAME &&
      call?.customer?.attributes?.LASTNAME
    );
  };


  const getBorderColor = () => {
    // Check if agent is on hold - yellow border
    if (call?.self?.held) return "static-yellow-500";

    // Check for active call states (resume/merge) - green border
    if (
      call?.self?.state === "ESTABLISHED" ||
      call?.self?.state === "CONNECTED"
    ) {
      return "static-green-500";
    }
    return "static-green-500";
  };

  return (
    <View
      borderColor="gray-200"
      borderWidth="thin"
      backgroundColor="gray-100"
      borderRadius={"regular"}
      UNSAFE_className="call-inProgress-container"
    >
      <View
        borderStartWidth="thicker"
        borderStartColor={getBorderColor()}
        borderRadius="regular"
        UNSAFE_className="call-inProgress-mid-container"
      >
        <Flex
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          height="100%"
        >
          <Flex direction="column">
            <Flex direction="row" alignItems="center" gap="size-100">
              <Text UNSAFE_className="call-inProgress-heading">
                {hasCustomerName() ? getCustomerName() : getCustomerPhone()}
              </Text>
            </Flex>
            {hasCustomerName() && (
              <Text UNSAFE_className="call-inProgress-font">
                {getCustomerPhone()}
              </Text>
            )}
            <Text UNSAFE_className="call-inProgress-font">
              <Flex
                direction="row"
                alignItems="center"
                justifyContent={"start"}
              >
                <Text marginX={3}>
                  {call?.self?.state === "RINGING" &&
                  call?.self?.direction === "INBOUND"
                    ? "Ringing"
                    : call?.self?.state !== "ESTABLISHED" &&
                      call?.self?.direction === "OUTBOUND"
                    ? "Dialing"
                    : call?.self?.held
                    ? "on hold"
                    : "on call"}
                </Text>
                <Text UNSAFE_className="call-inprogress-timer" marginX={2}>
                  {formattedTime(callTime)}
                </Text>
                <Text>
                  {call?.self?.direction === "OUTBOUND" &&
                    !(call.isSilentMonitor || call.isBarge) && (
                      <img
                        className="call-inProgress-icon"
                        src={OutgoingCallIcon}
                        alt="OutgoingCallIcon"
                      />
                    )}
                  {call?.self?.direction === "INBOUND" &&
                    !(call.isSilentMonitor || call.isBarge) && (
                      <img
                        className="call-inProgress-icon"
                        src={IncomingCallIcon}
                        alt="IncomingCallIcon"
                      />
                    )}
                  {(call.isSilentMonitor || call.isBarge) && (
                    <img
                      className="call-inProgress-icon"
                      src={EyeIcon}
                      alt="eyeIcon"
                    />
                  )}
                </Text>
              </Flex>
            </Text>
          </Flex>
          <Flex direction={"row"}>
            {conference?.id && call?.id && (
              <Flex
                direction={"column"}
                alignItems="center"
                UNSAFE_className={`call-action-items ${
                  call?.self?.muted ? "muted" : ""
                }`}
              >
                <TooltipTrigger delay={0}>
                  <div style={{ display: 'inline-block' }}>
                    <ActionButton
                      UNSAFE_className={
                        call?.self?.muted ? "iconButton active " : "iconButton"
                      }
                      UNSAFE_style={{
                        cursor:
                          disableTransfer() ||
                          (tabAccessControl("contact_monitor") === "true"
                            ? false
                            : call.beingBarged || (call.id && conference?.id && isAgentSelfCallHeld))
                            ? "not-allowed"
                            : "",
                        backgroundColor: "#eaeaea",
                        opacity:
                          (call.isSilentMonitor && !call.isBarge) ||
                          (tabAccessControl("contact_monitor") === "true"
                            ? false
                            : call.beingBarged) ||
                          (call.id && conference?.id && isAgentSelfCallHeld)
                            ? 0.5
                            : 1,
                      }}
                      onPress={
                        (call.isSilentMonitor && !call.isBarge) ||
                        (tabAccessControl("contact_monitor") === "true"
                          ? false
                          : call.beingBarged) ||
                        (call.id && conference?.id && isAgentSelfCallHeld)
                          ? () => {}
                          : () => muteCallHandler()
                      }
                      isQuiet
                    >
                      {call?.self?.muted ? <MuteIcon /> : <UnMmuteIcon />}
                    </ActionButton>
                  </div>
                  <Tooltip>
                    {(call.isSilentMonitor && !call.isBarge) ||
                    (tabAccessControl("contact_monitor") === "true"
                      ? false
                      : call.beingBarged) ||
                    (call.id && conference?.id && isAgentSelfCallHeld)
                      ? "Mute disabled"
                      : call?.self?.muted 
                        ? "Unmute customer call" 
                        : "Mute customer call"
                    }
                  </Tooltip>
                </TooltipTrigger>
                <Text UNSAFE_className="call-inProgress-font">
                  {call?.self?.muted ? "Unmute" : "Mute"}
                </Text>
              </Flex>
            )}
            <Flex
              direction={"column"}
              alignItems="center"
              UNSAFE_className="call-action-items"
            >
              <TooltipTrigger delay={0}>
                <div style={{ display: 'inline-block' }}>
                  <ActionButton
                    UNSAFE_className={"iconButton"}
                    onPress={
                      disableTransfer() ||
                      (tabAccessControl("contact_monitor") === "true"
                        ? false
                        : call.beingBarged) ||
                      (call.id && conference?.id && isAgentSelfCallHeld)
                        ? () => {}
                        : toggleHold
                    }
                    UNSAFE_style={{
                      cursor:
                        disableTransfer() ||
                        (tabAccessControl("contact_monitor") === "true"
                          ? false
                          : call.beingBarged || (call.id && conference?.id && isAgentSelfCallHeld))
                          ? "not-allowed"
                          : "",
                      backgroundColor: "#eaeaea",
                      opacity:
                        disableTransfer() ||
                        (tabAccessControl("contact_monitor") === "true"
                          ? false
                          : call.beingBarged) ||
                        (call.id && conference?.id && isAgentSelfCallHeld)
                          ? 0.5
                          : 1,
                    }}
                    isQuiet
                  >
                    {call?.self?.held ? <PlayIcon /> : <PauseIcon />}
                  </ActionButton>
                </div>
                <Tooltip>
                  {disableTransfer() ||
                  (tabAccessControl("contact_monitor") === "true"
                    ? false
                    : call.beingBarged) ||
                  (call.id && conference?.id && isAgentSelfCallHeld)
                    ? "Hold disabled"
                    : call?.self?.held 
                      ? "Resume customer call" 
                      : "Hold customer call"
                  }
                </Tooltip>
              </TooltipTrigger>
              <Text UNSAFE_className="call-inProgress-font">
                {call?.self?.held ? "Resume" : "Hold"}
              </Text>
            </Flex>
            <Flex
              direction={"column"}
              alignItems="center"
              UNSAFE_className="call-action-items"
            >
              <TooltipTrigger>
                <div style={{ display: 'inline-block' }}>
                  <ActionButton
                    UNSAFE_className={"call-end-Button"}
                    onPress={
                      (tabAccessControl("contact_monitor") === "true"
                        ? false
                        : call.beingBarged) ||
                      (call.id && conference?.id && isAgentSelfCallHeld)
                        ? () => {}
                        : hangupCallHandler
                    }
                    isQuiet
                    UNSAFE_style={{
                      background: "red",
                      cursor: (
                        tabAccessControl("contact_monitor") === "true"
                          ? false
                          : call.beingBarged || (call.id && conference?.id && isAgentSelfCallHeld)
                      )
                        ? "not-allowed"
                        : "pointer",
                      opacity: (
                        tabAccessControl("contact_monitor") === "true"
                          ? false
                          : call.beingBarged || (call.id && conference?.id && isAgentSelfCallHeld)
                      )
                        ? 0.5
                        : 1,
                    }}
                  >
                    <CallHangupIcon />
                  </ActionButton>
                </div>
                <Tooltip>
                  {(
                    tabAccessControl("contact_monitor") === "true"
                      ? false
                      : call.beingBarged || (call.id && conference?.id && isAgentSelfCallHeld)
                  )
                    ? "End call disabled"
                    : "End customer call"
                  }
                </Tooltip>
              </TooltipTrigger>
              <Text UNSAFE_className="call-inProgress-font">End</Text>
            </Flex>
          </Flex>
        </Flex>
      </View>
    </View>
  );
};

export default LiveCall;
